import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useQuery, useMutation } from 'react-query'
import { difference } from 'lodash'

import { FormFieldImage, parseImageURL } from '@prehook/shared'
import Question from './Question'
import Answer from './Answer'
import { createLinkTag, parseRecallVariables } from '@/utils'

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px 20px 20px;
  min-height: 100%;
  width: 100%;
  max-width: 1000px;
  margin: auto;
`

const isDiff = (before, after) => {
  const answerKeys = Object.keys(after)
  const questionType = answerKeys.includes('value') ? 'value' : 'selection'
  if (questionType === 'value' && before?.value !== after.value) {
    return true
  }
  if (questionType === 'selection') {
    const beforeSelection = before?.selection || []
    const afterSelection = after.selection
    const compareLeft = difference(beforeSelection, afterSelection)
    const compareRight = difference(afterSelection, beforeSelection)

    return compareLeft.length + compareRight.length !== 0
  }
  return false
}

const Content = ({
  subId,
  formField,
  quizName,
  integrations,
  nextPage,
  sub,
  handleLocalSub,
  setAllowNext,
  draft,
  history,
  shortId,
}) => {
  const {
    data: { data: quiz },
  } = useQuery('quiz')

  const { theme, store } = quiz

  const storeCurrency = store?.currency
  const domain = store?.domain

  const initialAnswerState = {
    type: formField.fieldType,
  }

  const [answer, setAnswer] = useState(initialAnswerState)
  const [valid, setValid] = useState(true)

  const updateCartAttributes = (shortId, subId) => {
    if (!draft) {
      const data = JSON.stringify({
        type: 'questionAnswered',
        quizId: shortId,
        submissionId: subId,
      })
      window.top.postMessage(data, '*')
    }
  }

  const updateSubmissionReq = async ({ answer, completed = false }) => {
    try {
      const body = {
        subId: sub.subId,
        shop: store.shopifyId,
        answer,
        formFieldId: formField.id,
        completed,
      }
      const url = `${process.env.GATSBY_API_URL}/sub`

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
      return response.json()
    } catch (error) {
      console.log(error)
    }
  }

  const sendIntegrationDataReq = async (answer) => {
    if (integrations.length > 0) {
      try {
        console.log({ answer })
        const body = {
          subResponses: sub.answers,
          answer,
          shopifyId: quiz?.store?.shopifyId,
          pubShortId: quiz?.pubShortId,
        }
        const url = `${process.env.GATSBY_API_URL}/integrations`

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        })
        return response.json()
      } catch (error) {
        console.log(error)
      }
    }
  }

  const sendGaEvent = ({ action, label, category }) => {
    if (!draft) {
      const gaIntegration = integrations?.find(
        (int) => int.integrationType === 'ANALYTICS'
      )

      if (!gaIntegration) return

      const data = JSON.stringify({
        type: 'analytics',
        gtag_id: gaIntegration.apiKey,
        gtag_action: action,
        gtag_label: label,
        gtag_category: category,
      })

      window.top.postMessage(data, '*')
    }
  }

  const [updateSubmission] = useMutation(updateSubmissionReq)
  const [sendIntegrationData] = useMutation(sendIntegrationDataReq)

  // This holds the intermediate state before persisting submission
  useEffect(() => {
    if (sub?.answers[formField.id]) {
      setAnswer(sub.answers[formField.id])
    }
    if (sub && Object.keys(sub.answers).length === 1) {
      // Note: could potentially count 'started' multiple times
      sendGaEvent({
        action: 'view',
        label: quizName,
        category: 'start',
      })
    }
  }, [sub])

  useEffect(() => {
    if (theme.font) {
      createLinkTag(theme.font, false)
    }
  }, [theme.font])

  useEffect(() => {
    if (answer.goToNext && isDiff(sub.answers[formField.id], answer)) {
      setTimeout(() => {
        onHandleSubmit()
      }, 500)
    }
  }, [answer])

  // Note: can't call this from inside the useEffect, get invalid hook error
  const parsedTitle = parseRecallVariables(shortId, formField.title)

  useEffect(() => {
    if (['THANK_YOU', 'PRODUCT_RECOMMENDATION'].includes(formField.fieldType)) {
      // only send completed if it is the first time on the formField
      if (!sub.answers[formField.id]) {
        const answer = { type: formField.fieldType, value: true }
        updateSubmission({
          completed: true,
          answer,
        })
        handleLocalSub(formField.id, answer, true)
      }
    }

    sendGaEvent({
      action: 'view',
      label: parsedTitle,
      category:
        formField.fieldType === 'PRODUCT_RECOMMENDATION'
          ? 'recommendations'
          : 'question',
    })
  }, [formField])

  const onHandleSubmit = () => {
    if (valid) {
      const statementTypeAnswers = [
        'WELCOME',
        'STATEMENT',
        'DISCOUNT_CODE',
        'THANK_YOU',
      ]

      if (!draft) {
        // emit update to cart on ph-script
        updateCartAttributes(shortId, sub.subId)
      }

      // set an answer value if it is a statement type question
      if (statementTypeAnswers.includes(answer.type) && !answer.value) {
        answer.value = true
      }
      // add https:// to link answer if not entered by shopper
      if (
        formField.fieldType === 'LINK' &&
        !/^https?\:\/\//.test(answer.value)
      ) {
        answer.value = 'https://' + answer.value
      }
      // only send request if answer has changed.
      if (isDiff(sub.answers[formField.id], answer)) {
        if (!draft) {
          updateSubmission({ answer })
          sendIntegrationData(answer)

          if (['EMAIL', 'PHONE_NUMBER'].includes(answer.type)) {
            sendGaEvent({
              action: 'submit',
              label: quizName,
              category: answer.type,
            })
          }
        }

        handleLocalSub(formField.id, answer, formField.required)
      }
      setAnswer(initialAnswerState)
      nextPage(answer)
    }
  }

  const imageURL = parseImageURL({
    image: formField.image,
    size: 'med',
    imageDomain: process.env.GATSBY_IMAGE_DOMAIN,
    bucketName: process.env.GATSBY_S3_BUCKET_NAME,
  })

  return (
    <ContentWrapper id="formfield-container">
      <Question
        formField={formField}
        theme={theme}
        shortId={draft ? quiz.shortId : quiz.pubShortId}
      />
      {imageURL && <FormFieldImage src={imageURL} alt={formField.fieldType} />}

      <Answer
        subId={subId}
        formField={formField}
        theme={theme}
        handleSubmit={onHandleSubmit}
        answer={answer}
        subAnswer={sub?.answers[formField.id]}
        handleAnswer={setAnswer}
        setValid={setValid}
        valid={valid}
        setAllowNext={setAllowNext}
        formFieldOptionIndex={formField.formFieldOptionIndex}
        shortId={draft ? quiz.shortId : quiz.pubShortId}
        draft={draft}
        storeCurrency={storeCurrency}
        domain={domain}
        // for product recommendations
        sendIntegrationData={sendIntegrationData}
        history={history}
        // for google analytics events
        quizName={quizName}
        sendGaEvent={sendGaEvent}
      />
    </ContentWrapper>
  )
}

export default Content
